import type { Ref } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Remove from '@mui/icons-material/Remove';
import { resolveTone } from './statusSeverity';

export interface DeltaChipProps {
  /** Signed change; sign drives the ▲/▼ arrow, magnitude is displayed. */
  delta: number;
  /**
   * Which direction reads as "good". `up` (default) tints a positive delta
   * green; `down` flips it (e.g. cost trending down is good).
   */
  goodDirection?: 'up' | 'down';
  /** Unit appended to the magnitude. Defaults to `%`. */
  suffix?: string;
  /** Decimal places for the magnitude. Defaults to 1. */
  fractionDigits?: number;
  /** Optional copy for the zero-delta case; when omitted, "No change" is shown. */
  noChangeLabel?: string;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * Compact directional delta (▲/▼ + magnitude), tinted by whether the movement
 * is good or bad. A zero delta shows a neutral dash.
 */
export function DeltaChip({
  delta,
  goodDirection = 'up',
  suffix = '%',
  fractionDigits = 1,
  noChangeLabel,
  ref,
}: DeltaChipProps) {
  const theme = useTheme();

  if (delta === 0) {
    const tone = resolveTone(theme, 'neutral');
    return (
      <Box
        component="span"
        ref={ref}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.375,
          fontFamily: theme.typography.button.fontFamily,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          color: tone.main,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Remove sx={{ fontSize: 12 }} />
        {noChangeLabel ?? 'No change'}
      </Box>
    );
  }

  const isGood = goodDirection === 'up' ? delta > 0 : delta < 0;
  const tone = resolveTone(theme, isGood ? 'success' : 'error');
  const Arrow = delta > 0 ? ArrowUpward : ArrowDownward;

  return (
    <Box
      component="span"
      ref={ref}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        fontFamily: theme.typography.button.fontFamily,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        color: tone.dark,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Arrow sx={{ fontSize: 12, color: tone.main }} />
      {Math.abs(delta).toFixed(fractionDigits)}
      {suffix}
    </Box>
  );
}
